import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PinLogin } from "./components/PinLogin";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy-load all pages for faster initial load
const ScheduleView = lazy(() => import("./pages/ScheduleView"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AppointmentsPage = lazy(() => import("./pages/AppointmentsPage"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ActivityPage = lazy(() => import("./pages/ActivityPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const ClientProfilePage = lazy(() => import("./pages/ClientProfilePage"));
const ShortLinkResolver = lazy(() => import("./pages/ShortLinkResolver"));

const PageLoader = () => (
  <div className="min-h-screen bg-stone-50 flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false, permKey = null }) => {
  const { isAuthenticated, isAdmin, canManageClients, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <PinLogin />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (permKey === 'canManageClients' && !canManageClients) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Routes
const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={
          <ProtectedRoute>
            <ScheduleView />
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={
          <ProtectedRoute>
            <ScheduleView />
          </ProtectedRoute>
        } />
        <Route path="/services" element={
          <ProtectedRoute adminOnly>
            <ServicesPage />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        } />
        <Route path="/payments" element={
          <ProtectedRoute>
            <PaymentsPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute adminOnly>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/activity" element={
          <ProtectedRoute adminOnly>
            <ActivityPage />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute permKey="canManageClients">
            <ClientsPage />
          </ProtectedRoute>
        } />
        <Route path="/clients/:contactId" element={
          <ProtectedRoute permKey="canManageClients">
            <ClientProfilePage />
          </ProtectedRoute>
        } />
        {/* Public booking page — no auth required */}
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/s/:code" element={<ShortLinkResolver />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
